import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MUSIC_VIDEO_CUTS_PER_SONG } from 'fym-shared/src/music24/musicVideoDoc';
import { useIsDemoAccount } from '../accountSelector/useIsDemoAccount';
import { getFullAPIFileURL } from '../common/getFullAPIFileURL';
import { TypedMemo } from '../common/types';
import { ErrorView } from '../common/views/ErrorView/ErrorView';
import { LoadingView } from '../common/views/LoadingView';
import { safeReturnPath } from '../coverEditor/coverEditorPath';
import { useRealtimePollInterval } from '../realtime/useRealtimePollInterval';
import { catalogSongPath } from '../smartLinks/catalog/song/page/catalogSongPath';
import { EDITOR_POLL_MS } from './editorPolling';
import { stateMessages } from './messages/stateMessages';
import { MusicVideoCutLoader } from './MusicVideoCutLoader';
import { useGetMusicVideoEditorQuery } from './musicVideoEditorApi';
import { NoMasterState } from './states/NoMasterState';
import { openCutChoice } from './states/openCutChoice';
import { useFirstCut } from './states/useFirstCut';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from './MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface MusicVideoEditorScreenProps {
  accountId: number;
  catalogItemUuid: string;
}

/**
 * The route body of the music video editor (design b1, e1, e6): it reads the song, its song map,
 * its lyrics and its cuts (`GET .../music-video/:catalogItemUuid`); with no master it shows e6; a
 * song with no cut gets its first cut (the editor never opens empty); then it opens the cut of
 * `?cut=` or the newest cut. `musicVideo.cut.updated` and `catalog.song.updated` read it again.
 */
const MusicVideoEditorScreenComponent: React.FC<MusicVideoEditorScreenProps> = ({ accountId, catalogItemUuid }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const readOnly = useIsDemoAccount();
  const editor = useGetMusicVideoEditorQuery({ accountId, catalogItemUuid });
  const raw = editor.data;
  const isWaiting =
    raw !== undefined && (raw.songMap === null || raw.cuts.some((cut) => cut.directorState === 'working'));
  const pollingInterval = useRealtimePollInterval(isWaiting ? EDITOR_POLL_MS : 0);
  useGetMusicVideoEditorQuery({ accountId, catalogItemUuid }, { pollingInterval, skip: pollingInterval === 0 });

  // The cover and the poster links work on the API origin (`getFullAPIFileURL` keeps a full link).
  const data = useMemo(() => {
    if (raw === undefined) {
      return undefined;
    }
    const { artworkUrl } = raw.song;
    return { ...raw, song: { ...raw.song, artworkUrl: artworkUrl === null ? null : getFullAPIFileURL(artworkUrl) } };
  }, [raw]);
  const first = useFirstCut({ accountId, catalogItemUuid, data, enabled: !readOnly });
  const cutUuid = data === undefined ? null : openCutChoice(data.cuts, params.get('cut'), first.createdUuid);

  const leave = useCallback(() => {
    const from = safeReturnPath(params.get('from'));
    // eslint-disable-next-line no-void -- a router navigation: nothing to wait for
    void navigate(from ?? catalogSongPath(catalogItemUuid, { section: 'details', accountId }));
  }, [accountId, catalogItemUuid, navigate, params]);
  const openCut = useCallback(
    (uuid: string) => {
      const next = new URLSearchParams(params);
      next.set('cut', uuid);
      setParams(next, { replace: true });
    },
    [params, setParams]
  );

  if (editor.error !== undefined && data === undefined) {
    return (
      <div className={styles.fullState}>
        <ErrorView error={editor.error} retry={() => editor.refetch().catch(console.error)} />
      </div>
    );
  }
  if (first.isFailed) {
    return (
      <div className={styles.fullState}>
        <ErrorView
          error={
            new Error(
              first.error === 'limit'
                ? intl.formatMessage(stateMessages.cutLimit, { count: MUSIC_VIDEO_CUTS_PER_SONG })
                : intl.formatMessage(stateMessages.firstFailed)
            )
          }
          retry={first.retry}
        />
      </div>
    );
  }
  if (data !== undefined && (data.songMap === null || data.song.durationSec === null)) {
    return (
      <div className={styles.fullState}>
        <NoMasterState accountId={accountId} catalogItemUuid={catalogItemUuid} title={data.song.title} onBack={leave} />
      </div>
    );
  }
  if (data === undefined || data.songMap === null || cutUuid === null) {
    return (
      <div className={styles.fullState} role="status">
        <LoadingView label={intl.formatMessage(stateMessages.opening)} />
      </div>
    );
  }
  return (
    <MusicVideoCutLoader
      key={cutUuid}
      accountId={accountId}
      catalogItemUuid={catalogItemUuid}
      cutUuid={cutUuid}
      data={data}
      songMap={data.songMap}
      readOnly={readOnly}
      onLeave={leave}
      onOpenCut={openCut}
    />
  );
};

export const MusicVideoEditorScreen = TypedMemo(MusicVideoEditorScreenComponent);
